import axios from "axios";
import { getApiBaseUrl } from "@ayla/config";
// CSRF-unsafe methods that require token
const UNSAFE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);
/**
 * Creates a new Axios instance pre-configured with the standard Ayla API
 * settings: correct base URL, JSON headers, credentials, and the CSRF
 * request interceptor.
 *
 * Use this in app-level code when you need an instance you can attach your own
 * interceptors to without mutating the shared singleton.
 *
 * @example
 * // apps/engage/src/lib/apiClient.ts
 * import { createApiClient } from "@ayla/api";
 * export const apiClient = createApiClient();
 * apiClient.interceptors.response.use(...); // safe — only affects this instance
 */
export function createApiClient() {
    const instance = axios.create({
        baseURL: getApiBaseUrl(),
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
        },
        withCredentials: true,
    });
    instance.interceptors.request.use((config) => {
        if (config.method && UNSAFE_METHODS.has(config.method.toUpperCase())) {
            const csrfToken = getCsrfToken();
            if (csrfToken) {
                config.headers.set("X-CSRF-Token", csrfToken);
            }
        }
        return config;
    }, (error) => Promise.reject(error));
    instance.interceptors.response.use(response => response, (error) => {
        if (error.response?.status === 401) {
            console.warn("[API] 401 Unauthorized detected");
        }
        if (error.response?.status === 403) {
            console.warn("[API] 403 Forbidden - possible CSRF issue");
        }
        return Promise.reject(error);
    });
    return instance;
}
/** Shared singleton — kept for backwards compatibility with existing consumers. */
export const apiClient = createApiClient();
/**
 * Get CSRF token from cookie or meta tag
 */
function getCsrfToken() {
    // Try meta tag first (common in SSR apps)
    if (typeof document !== "undefined") {
        const metaTag = document.querySelector('meta[name="csrf-token"]');
        if (metaTag) {
            return metaTag.getAttribute("content");
        }
        // Try cookie (Django/Flask style)
        const cookies = document.cookie.split(";");
        for (const cookie of cookies) {
            const [name, value] = cookie.trim().split("=");
            if (name === "csrftoken" || name === "XSRF-TOKEN") {
                return decodeURIComponent(value);
            }
        }
    }
    return null;
}
export default apiClient;
